import { useSession } from "@/account/lib/auth-client";
import { trpc } from "@/account/lib/trpc";
import { Link } from "react-router";
import { ArrowRight, Building2, CreditCard, User } from "lucide-react";
import { Button } from "@nosible/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";

export function HomePage() {
  const { data: session } = useSession();
  const { data: profile, isLoading } = trpc.user.getProfile.useQuery(undefined, {
    enabled: !!session,
  });

  const userName = session?.user?.name?.trim() || session?.user?.email || "there";
  const firstName = userName.split(/\s+/)[0] || "there";
  const organizations = profile?.organizations ?? [];
  const subscriptions = profile?.subscriptions ?? [];
  const visibleSubscriptions = subscriptions.filter((subscription) => {
    const status = subscription.status.toLowerCase();
    return status !== "canceled" && status !== "cancelled";
  });
  const primarySubscription = visibleSubscriptions[0];
  const primarySubscriptionId = primarySubscription?.id;
  const { data: currentUsage } = trpc.subscription.usage.currentUsage.useQuery(
    { subscriptionId: primarySubscriptionId ?? "" },
    { enabled: Boolean(session && primarySubscriptionId) },
  );
  const requestsThisPeriod = currentUsage?.usage?.splits?.reduce(
    (total: number, entry: { used: number }) => total + entry.used,
    0,
  ) ?? 0;

  return (
    <div className="account-ui-page account-overview space-y-6">
      <section className="account-overview__hero">
        <div>
          <p className="brand-kicker">ACCOUNT / OVERVIEW</p>
          <h1>WELCOME BACK, {firstName.toUpperCase()}</h1>
          <p className="account-overview__intro">
            Manage your profile, organizations, team access, subscriptions, and API credentials.
          </p>
        </div>
        <dl className="account-overview__metrics" aria-label="Account summary">
          <div>
            <dt>ORGANIZATIONS</dt>
            <dd>{isLoading ? "—" : organizations.length.toString().padStart(2, "0")}</dd>
          </div>
          <div>
            <dt>SUBSCRIPTIONS</dt>
            <dd>{isLoading ? "—" : visibleSubscriptions.length.toString().padStart(2, "0")}</dd>
          </div>
          <div>
            <dt>REQUESTS THIS PERIOD</dt>
            <dd>{isLoading || !primarySubscriptionId ? "—" : requestsThisPeriod.toLocaleString()}</dd>
          </div>
          <div>
            <dt>ACCOUNT STATUS</dt>
            <dd className="is-status">ACTIVE</dd>
          </div>
        </dl>
      </section>

      <section aria-labelledby="manage-heading">
        <div className="grid gap-5 md:grid-cols-3">
          <Card>
            <CardHeader>
              <User className="brand-card-icon" aria-hidden="true" />
              <CardTitle>Account</CardTitle>
              <CardDescription>
                Update your name, email, notifications, and personal account preferences in one place.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="account-overview__card-footer">
                <span className="account-overview__card-meta account-overview__card-meta--email">{session?.user?.email || "ACCOUNT"}</span>
                <Button asChild variant="outline" className="account-overview__card-action">
                  <Link to="/account">MANAGE ACCOUNT <ArrowRight /></Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Building2 className="brand-card-icon" aria-hidden="true" />
              <CardTitle>Organizations</CardTitle>
              <CardDescription>
                Invite teammates, manage member access, and administer your organization subscriptions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="account-overview__card-footer">
                <span className="account-overview__card-meta">{organizations.length} ORGANIZATIONS</span>
                <Button asChild variant="outline" className="account-overview__card-action">
                  <Link to="/organizations">VIEW <ArrowRight /></Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CreditCard className="brand-card-icon" aria-hidden="true" />
              <CardTitle>Subscriptions</CardTitle>
              <CardDescription>
                Review plans, change or cancel billing, issue API keys, and monitor usage.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="account-overview__card-footer">
                <span className="account-overview__card-meta">
                  {primarySubscription ? (primarySubscription.name || primarySubscription.plan.name).toUpperCase() : "NO ACTIVE SUBSCRIPTIONS"}
                </span>
                <Button asChild variant="outline" className="account-overview__card-action">
                  <Link to="/subscriptions">VIEW <ArrowRight /></Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
